import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
// The default typings on Radix wrappers omit className/children props on some
// forwarded-ref components; re-declare the ones we use so the compiler is happy.
const ADContent = AlertDialogContent as unknown as React.FC<{ children?: React.ReactNode; className?: string }>;
const ADHeader = AlertDialogHeader as unknown as React.FC<{ children?: React.ReactNode; className?: string }>;
const ADFooter = AlertDialogFooter as unknown as React.FC<{ children?: React.ReactNode; className?: string }>;
const ADTitle = AlertDialogTitle as unknown as React.FC<{ children?: React.ReactNode; className?: string }>;
const ADDescription = AlertDialogDescription as unknown as React.FC<{ children?: React.ReactNode; className?: string }>;
const ADAction = AlertDialogAction as unknown as React.FC<{ children?: React.ReactNode; className?: string; onClick?: () => void; 'data-testid'?: string }>;
const ADCancel = AlertDialogCancel as unknown as React.FC<{ children?: React.ReactNode; className?: string; 'data-testid'?: string }>;
import { toast } from 'sonner';
import {
  Plus, Pencil, Trash2, Calculator, Link2, ChevronRight, Search,
  Layers, Building2, BadgeCheck, AlertTriangle,
} from 'lucide-react';

interface MatrixRow {
  id: string;
  name: string;
  description: string;
  insurer_id: string;
  engine: string;
  is_published: boolean;
  current_version_number?: number;
  linked_template_count: number;
  factors_count: number;
  created_at?: string;
  created_by_email?: string;
  updated_at?: string;
}

export const RiskMatricesPage: React.FC = () => {
  const nav = useNavigate();
  const [rows, setRows] = useState<MatrixRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');
  const [renaming, setRenaming] = useState<MatrixRow | null>(null);
  const [renameValue, setRenameValue] = useState('');
  const [confirmDelete, setConfirmDelete] = useState<MatrixRow | null>(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get<MatrixRow[]>('/risk/matrices');
      setRows(r.data);
    } catch {
      toast.error('Failed to load risk matrices');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { void load(); }, []);

  const filtered = rows.filter((r) =>
    !filter.trim() || r.name.toLowerCase().includes(filter.toLowerCase())
    || (r.description || '').toLowerCase().includes(filter.toLowerCase()),
  );

  const doRename = async () => {
    if (!renaming) return;
    if (!renameValue.trim() || renameValue.length < 2) {
      toast.error('Name must be at least 2 characters.');
      return;
    }
    try {
      await api.put(`/risk/matrices/${renaming.id}`, { name: renameValue.trim() });
      toast.success(`Renamed to "${renameValue.trim()}"`);
      setRenaming(null);
      await load();
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail ?? 'Rename failed';
      toast.error(msg);
    }
  };

  const doDelete = async () => {
    if (!confirmDelete) return;
    try {
      await api.delete(`/risk/matrices/${confirmDelete.id}`);
      toast.success(`Archived "${confirmDelete.name}"`);
      setConfirmDelete(null);
      await load();
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail ?? 'Archive failed';
      toast.error(msg);
    }
  };

  return (
    <div className="space-y-4 animate-fade-in" data-testid="risk-matrices-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h2 className="font-headline text-2xl text-raptor-navy tracking-wide">Risk matrices</h2>
          <p className="text-sm text-raptor-navy/60 font-body max-w-2xl">
            Named rating tables that price risk for one or more survey templates. Each matrix has an
            immutable version history — editing produces a new version; historical assessments stay bound
            to the version they were scored under.
          </p>
        </div>
        <Button variant="gradient" onClick={() => nav('new')} data-testid="new-matrix-btn">
          <Plus size={16} className="mr-1" /> New matrix
        </Button>
      </div>

      <div className="flex items-center gap-2 max-w-md">
        <Search size={14} className="text-raptor-navy/50" />
        <Input
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder="Filter by name or description…"
          className="h-8 text-sm"
          data-testid="matrix-filter"
        />
      </div>

      {loading ? (
        <div className="text-raptor-navy/50 text-center py-10" data-testid="matrices-loading">Loading matrices…</div>
      ) : filtered.length === 0 ? (
        <Card>
          <CardContent className="py-10 text-center space-y-2">
            <Layers className="mx-auto text-raptor-navy/30" size={32} />
            <div className="font-body text-raptor-navy/60">
              {rows.length === 0
                ? 'No risk matrices yet. Create one to start scoring risks against a defined rubric.'
                : 'No matrices match your filter.'}
            </div>
            {rows.length === 0 && (
              <Button variant="gradient" size="sm" onClick={() => nav('new')} data-testid="empty-state-new-matrix">
                <Plus size={14} className="mr-1" /> Create your first matrix
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-3 md:grid-cols-2">
          {filtered.map((m) => (
            <Card key={m.id} className="hover:shadow-md transition-shadow" data-testid={`matrix-card-${m.id}`}>
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <CardTitle className="text-base flex items-center gap-2">
                      <Calculator size={16} className="text-raptor-blue shrink-0" />
                      <span className="truncate">{m.name}</span>
                    </CardTitle>
                    {m.description && (
                      <CardDescription className="mt-1 line-clamp-2">{m.description}</CardDescription>
                    )}
                  </div>
                  <div className="flex flex-col items-end gap-1 shrink-0">
                    <Badge variant="blue">v{m.current_version_number ?? 1}</Badge>
                    {m.is_published ? (
                      <Badge variant="success" className="text-[10px]"><BadgeCheck size={10} /> Published</Badge>
                    ) : (
                      <Badge variant="muted" className="text-[10px]">Draft</Badge>
                    )}
                  </div>
                </div>
              </CardHeader>
              <CardContent className="pt-0 space-y-3">
                <div className="flex flex-wrap gap-3 text-xs text-raptor-navy/70 font-body">
                  <span className="flex items-center gap-1">
                    <Building2 size={12} /> {m.engine}
                  </span>
                  <span className="flex items-center gap-1">
                    <Layers size={12} /> {m.factors_count} factor{m.factors_count === 1 ? '' : 's'}
                  </span>
                  <span className="flex items-center gap-1">
                    <Link2 size={12} /> {m.linked_template_count} template{m.linked_template_count === 1 ? '' : 's'}
                  </span>
                </div>
                <div className="flex items-center gap-1">
                  <Button
                    variant="gradient"
                    size="sm"
                    onClick={() => nav(m.id)}
                    data-testid={`open-matrix-${m.id}`}
                  >
                    Open editor <ChevronRight size={14} className="ml-0.5" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => { setRenaming(m); setRenameValue(m.name); }}
                    data-testid={`rename-matrix-${m.id}`}
                  >
                    <Pencil size={12} className="mr-1" /> Rename
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setConfirmDelete(m)}
                    disabled={m.linked_template_count > 0}
                    title={m.linked_template_count > 0 ? 'Unlink templates first' : 'Archive matrix'}
                    data-testid={`archive-matrix-${m.id}`}
                  >
                    <Trash2 size={12} className="mr-1" /> Archive
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Rename dialog */}
      <AlertDialog open={!!renaming} onOpenChange={(o) => !o && setRenaming(null)}>
        <ADContent>
          <ADHeader>
            <ADTitle>Rename matrix</ADTitle>
            <ADDescription>
              Rename “{renaming?.name}”. Version history and linked templates are unaffected.
            </ADDescription>
          </ADHeader>
          <Input
            value={renameValue}
            onChange={(e) => setRenameValue(e.target.value)}
            placeholder="Matrix name"
            data-testid="rename-matrix-input"
          />
          <ADFooter>
            <ADCancel data-testid="rename-cancel">Cancel</ADCancel>
            <ADAction onClick={() => void doRename()} data-testid="rename-confirm">Save</ADAction>
          </ADFooter>
        </ADContent>
      </AlertDialog>

      {/* Archive dialog */}
      <AlertDialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
        <ADContent>
          <ADHeader>
            <ADTitle className="flex items-center gap-2">
              <AlertTriangle className="text-amber-500" size={18} /> Archive this matrix?
            </ADTitle>
            <ADDescription>
              “{confirmDelete?.name}” will be archived. Existing assessments continue to reference their
              frozen version. This action can be reversed by a database admin if needed.
            </ADDescription>
          </ADHeader>
          <ADFooter>
            <ADCancel data-testid="archive-cancel">Cancel</ADCancel>
            <ADAction onClick={() => void doDelete()} data-testid="archive-confirm">Archive</ADAction>
          </ADFooter>
        </ADContent>
      </AlertDialog>
    </div>
  );
};

export default RiskMatricesPage;
